import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Preloader from "~/components/Preloader";
import useCourseLevelsSelectOptions from "~/entity/Courses/hooks/useCourseLevelsSelectOptions";
import Wrapper from "~/shared/ui/Wrapper";
import { CoursesResponse, useCourses } from "~/entity/Courses";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { coursesTableHeader } from "~/pages/Editor/EditorCatalogEducationalPrograms/options";
import { OpenModal } from "~/pages/Editor/type";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { getLocalizedSortableApiKeys } from "~/shared/lib/localizedSortField";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";

import CoursesFilter from "./CoursesFilter";
import { CoursesManagementHoc as CoursesManagement } from "./CoursesManagement/CoursesManagementHoc";
import {
  coursesSortFieldForApi,
  getEditorCoursesColumns,
} from "./model/tableAdapter";

import s from "~/pages/Editor/EditorCatalogEducationalPrograms/EntityPages.less";

const courseFormId = "CourseForm";

const EditorCourses = () => {
  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setData = useSetLocationData();
  const setDataReplace = useSetLocationData(true);
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermissions();
  const { isLoading } = useCourseLevelsSelectOptions();

  const {
    count,
    page,
    openModal,
    sortField,
    sortType: sortTypeRaw,
  } = useGetLocationData();
  // Ключи фильтров приходят из URL и не описаны в типе useGetLocationData
  const { ActualOn, code, codeIS, name, status } =
    useGetLocationData() as Record<string, unknown>;

  const sortableApiKeys = (() => {
    const lang = localStorage.getItem("language");
    return getLocalizedSortableApiKeys(coursesTableHeader, lang);
  })();

  // Серверная сортировка: бэкенд courses принимает sortField/sortType,
  // в URL хранится только её состояние
  const { sortType: normalizedSortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: sortableApiKeys,
    setData,
  });

  const tableSortField =
    sortField && sortableApiKeys.includes(sortField) ? sortField : undefined;

  const [getCourses, setGetCourses] = useState<CoursesResponse[] | undefined>();
  const [getCoursesLoading, setGetCoursesLoading] = useState<boolean>(false);
  const [getCoursesTotal, setGetCoursesTotal] = useState<number>(25);
  const { coursesGet } = useCourses();

  const { handleCount, handlePage } = usePaginationParams();

  const closeModal = () => setDataReplace("openModal", "", "currentId", "");

  const today = new Date();
  const defaultActualOn = today.toISOString().split("T")[0];

  const columns = useMemo(() => {
    const lang = localStorage.getItem("language");
    return getEditorCoursesColumns(
      t,
      coursesTableHeader,
      sortField,
      normalizedSortType,
      lang,
    );
  }, [t, sortField, normalizedSortType, i18n.language]);

  const fetchCourses = async () => {
    setGetCoursesLoading(true);
    const apiSortField = coursesSortFieldForApi(sortField);
    try {
      const res = await coursesGet({
        actualOn:
          (ActualOn &&
            new Date(String(ActualOn)).toISOString().split("T")[0]) ||
          defaultActualOn,
        code: code ? String(code) : undefined,
        codeIS: codeIS ? String(codeIS) : undefined,
        name: name ? String(name) : undefined,
        status: status ? String(status) : undefined,
        sortField: apiSortField,
        sortType: apiSortField ? normalizedSortType : undefined,
        page,
        count,
      });
      setGetCourses(res.data?.courses);
      setGetCoursesTotal(res.data?.total);
    } finally {
      setGetCoursesLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, [
    sortField,
    normalizedSortType,
    page,
    count,
    ActualOn,
    code,
    codeIS,
    name,
    status,
  ]);

  const openAddModal = () => setDataReplace("openModal", OpenModal.Add);

  const handleRowEdit = (record: { instanceId?: string | number }) => {
    setDataReplace("openModal", OpenModal.Edit, "currentId", record.instanceId);
  };

  if (getCoursesLoading || isLoading) {
    return <Preloader />;
  }

  return (
    <Wrapper.Content
      title={t("cep.courses")}
      txtBtn={t("ui.back")}
      goBackHandler={() => history.replace("/editor/cep/")}
      filterComponent={<CoursesFilter />}
      controlComponent={
        hasPermission("employeeAccounting.curriculum.course.create") ? (
          <Button
            onClick={openAddModal}
            size={"middle"}
            icon={<PlusIcon size={16} />}
          >
            {t("ui.add")}
          </Button>
        ) : undefined
      }
    >
      <TableV2
        columns={columns}
        dataSource={getCourses}
        rowKey={(record) => record.instanceId}
        sortableColumns={sortableApiKeys}
        sortField={tableSortField}
        sortType={tableSortField ? normalizedSortType : undefined}
        onSortChange={onSortChange}
        {...(hasPermission("employeeAccounting.curriculum.course.update")
          ? { onRowEdit: handleRowEdit }
          : {})}
        wrapperClassName={s.editor_table}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={getCoursesTotal}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
      <Modal
        open={openModal === OpenModal.Edit || openModal === OpenModal.Add}
        title={t(
          openModal === OpenModal.Add ? "cep.add_course" : "cep.edit_course",
        )}
        onCancel={closeModal}
        closable
        centered
        destroyOnHidden
        okButton={{
          htmlType: "submit",
          form: courseFormId,
          children: t("ui.save"),
        }}
      >
        <CoursesManagement
          formId={courseFormId}
          refetch={fetchCourses}
          closeModal={closeModal}
        />
      </Modal>
    </Wrapper.Content>
  );
};
export default EditorCourses;
